/**
 * Analysis Room (the Insights Workshop until 2026-10-21) — the club-facing home for Insights Lab work that is
 * ready for customers: Analytics Collaboration (with Tables and Ask AI),
 * Build a dashboard and Segment Workbench, on the club's own layer-stack
 * games.
 *
 * Shares NO code with the Insights Lab (frontend/src/workshop is
 * re-implemented, and eslint refuses Lab imports here).
 *
 * Who decides: Collaboration, Tables and Ask AI go through
 * /api/v1/workshop, where app/workshop/access.py decides every request.
 * Build a dashboard and Segment Workbench read the match page's own API
 * (/api/v1/game-match-summary/*) under its club authorization — the data a
 * club's match page already shows; their capabilities gate the tab only.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { trackEvent } from '../lib/analytics'
import { ApiError, api, type Session } from '../lib/api'
import { pathSeg } from '../lib/urls'
import { openViewerWindow } from '../packs/videoHandoff'
import { useViewerSeek } from '../pages/match/viewerBridge'
import CanvasWorkspace from './canvas/CanvasWorkspace'
import ChatPanel from './canvas/ChatPanel'
import {
  createProject, deleteProject, listProjects, registerGame, updateProject,
  type AnalyticsProject, type CanvasDraft,
} from './lib/workshopApi'
import '../pages/admin.css'
import '../pages/insights-lab.css'
import '../pages/insights-playground.css'
import './workshop.css'

const LabDashboard = lazy(() => import('./dashboard/LabDashboard'))
const SegmentWorkbench = lazy(() => import('./segment/SegmentWorkbench'))
const DataDictionary = lazy(() => import('./dictionary/DataDictionary'))
const AttackingStructure = lazy(() => import('./analyses/AttackingStructure'))
const DefensiveControl = lazy(() => import('./analyses/DefensiveControl'))
const ProvokePressing = lazy(() => import('./analyses/ProvokePressing'))
const FormationDynamics = lazy(() => import('./formation/FormationDynamics'))

export type WorkshopTab = 'collaborate' | 'dashboard' | 'segment' | 'dictionary'
  | 'attacking' | 'defensive' | 'provoke' | 'formations'

// `hidden`: routable, no tab in the bar. The entry still belongs here — the
// route resolves against this list and falls back to the first tab.
const TABS: { tab: WorkshopTab; path: string; label: string; cap: string; hidden?: boolean }[] = [
  { tab: 'collaborate', path: '/analysis-room', label: 'Queries', cap: 'collaborate' },
  { tab: 'dashboard', path: '/analysis-room/dashboard', label: 'Dashboards', cap: 'dashboard' },
  { tab: 'segment', path: '/analysis-room/segment', label: 'Segments', cap: 'segment' },
  // The four analyses: each reads the serve API (insights/<key>,
  // formation-dynamics), which enforces the same capability on the data
  // (serve_api.WORKSHOP_GATED) — the tab and the rows go together.
  { tab: 'attacking', path: '/analysis-room/attacking-structure',
    label: 'Attacking Structure', cap: 'attacking_structure' },
  { tab: 'defensive', path: '/analysis-room/defensive-control',
    label: 'Defensive Control', cap: 'defensive_control' },
  { tab: 'provoke', path: '/analysis-room/provoke-pressing',
    label: 'Provoke Pressing', cap: 'provoke_pressing' },
  { tab: 'formations', path: '/analysis-room/formation-dynamics',
    label: 'Formation Dynamics', cap: 'formation_dynamics' },
  // Reached from the 'Data dictionary' link in the Queries tab's table
  // browser, where the question comes up. `tables` gates the TAB; the
  // endpoint is schema only, served to every signed-in reader on purpose
  // (tests/test_serve_api.py::test_the_dictionary_is_served; the data
  // export ships the same text).
  { tab: 'dictionary', path: '/analysis-room/dictionary', label: 'Data dictionary',
    cap: 'tables', hidden: true },
]

type Access = { org_id: number | null; enabled: boolean; capabilities: string[]; staff: boolean }
type Game = { game_id: number; game_code: string; date: string | null }

export default function InsightsWorkshop({ session, tab = 'collaborate' }: {
  session: Session; tab?: WorkshopTab
}) {
  const orgId = session.org?.id ?? null
  const viewingAs = !!session.impersonating
  const [access, setAccess] = useState<Access | null>(null)
  const [games, setGames] = useState<Game[] | null>(null)
  // The selected game rides the URL (?game=<id>) so a refresh, a shared
  // link or a reopened tab lands on the SAME match, not the newest one
  // (Ben, 2026-10-15). The id still has to survive the games-list check
  // below before anything trusts it.
  const [searchParams, setSearchParams] = useSearchParams()
  const [gameId, setGameId] = useState<number | null>(() => {
    const v = Number(searchParams.get('game'))
    return Number.isInteger(v) && v > 0 ? v : null
  })
  useEffect(() => {
    // The Workshop was never instrumented on the SPA (Mixpanel audit,
    // 2026-10-20): one event per game opened in it. Query/Ask volumes
    // are tracked at the API client (workshopApi.ts).
    if (gameId != null) trackEvent('Workshop Opened', { game_id: gameId })
  }, [gameId])
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (gameId == null) next.delete('game')
      else next.set('game', String(gameId))
      return next
    }, { replace: true })
    // setSearchParams churns identity every render; depending on it here
    // would loop the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId])
  const [projects, setProjects] = useState<AnalyticsProject[]>([])
  const [active, setActive] = useState<AnalyticsProject | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const addCardRef = useRef<((question: string, sql: string, visual?: string, explanation?: string) => void) | null>(null)
  const toggleTablesRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    let alive = true
    setAccess(null)
    api<Access>(`/api/v1/workshop/access${orgId ? `?org_id=${orgId}` : ''}`)
      .then((a) => { if (alive) setAccess(a) })
      .catch(() => { if (alive) setAccess({ org_id: orgId, enabled: false, capabilities: [], staff: false }) })
    return () => { alive = false }
  }, [orgId])

  useEffect(() => {
    if (!access?.enabled || orgId == null) return
    let alive = true
    setGames(null)
    api<{ games: Game[] }>(`/api/v1/workshop/games?org_id=${orgId}`)
      .then((d) => {
        if (!alive) return
        for (const g of d.games) registerGame(orgId, g.game_code, g.game_id)
        setGames(d.games)
        setGameId((cur) => (cur && d.games.some((g) => g.game_id === cur))
          ? cur : d.games[0]?.game_id ?? null)
      })
      .catch((e: unknown) => { if (alive) { setGames([]); setError(msgOf(e)) } })
    return () => { alive = false }
  }, [access?.enabled, orgId])

  const game = games?.find((g) => g.game_id === gameId) ?? null

  const canCollaborate = !!access?.capabilities.includes('collaborate')
  // A match with no canvas gets none made for it: the page says to press
  // + New Canvas. Seeding one per match on every open left a blank canvas
  // on each stack match anyone visited (Ben, 2026-10-16).
  // Only the newest list may land: switching club or game starts a new one,
  // and an older answer arriving after it (an empty list for the previous
  // game) must not replace it — it did, and New Canvas then picked a name a
  // teammate's canvas already had (409, dev 2026-10-16).
  const listSeq = useRef(0)
  // The game only once the club's own list confirms it (`game`): the URL's
  // ?game= can still name another club's match while the club changes, and
  // listing a canvas there answers nothing but a 404.
  const validGameId = game?.game_id ?? null
  const refreshProjects = useCallback(async (keep?: number) => {
    // Canvases are Collaboration's: a club without it never asks.
    if (orgId == null || validGameId == null || !canCollaborate) return
    const seq = ++listSeq.current
    const stale = () => seq !== listSeq.current
    try {
      const list = await listProjects(orgId, validGameId)
      if (stale()) return
      const withCode = list.map((p) => ({ ...p, game_code: game?.game_code ?? null }))
      setProjects(withCode)
      setActive(withCode.find((p) => p.id === keep) ?? withCode.find((p) => p.can_edit) ?? withCode[0] ?? null)
    } catch (e) {
      if (!stale()) setError(msgOf(e))
    }
  }, [orgId, validGameId, game?.game_code, canCollaborate])

  useEffect(() => { void refreshProjects() }, [refreshProjects])

  // The name is chosen from the game's canvases as the SERVER has them now,
  // not the picker's copy (which can lag a teammate, or a race above), and a
  // name taken in the moment between is tried once more with a fresh list.
  const newCanvas = async () => {
    if (orgId == null || validGameId == null) return
    setError(null)
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const taken = (await listProjects(orgId, validGameId)).map((c) => c.title)
        const p = await createProject({
          title: nextCanvasTitle(game?.game_code ?? 'Game', taken),
          org_id: orgId, game_id: validGameId, draft: { cards: [] },
        })
        await refreshProjects(p.id)
        return
      } catch (e) {
        if (statusOf(e) === 409 && attempt === 0) continue
        setError(msgOf(e))
        return
      }
    }
  }

  // Rename and delete are the owner's (the server refuses anyone else with a
  // 404); a teammate's canvas offers neither.
  const renameCanvas = async () => {
    if (!active?.can_edit) return
    const title = window.prompt('Rename this canvas', active.title)?.trim()
    if (!title || title === active.title) return
    if (projects.some((p) => p.id !== active.id
                       && p.title.trim().toLowerCase() === title.toLowerCase())) {
      setError(`A canvas named "${title}" already exists for this game — choose another name.`)
      return
    }
    try {
      await updateProject(active.id, { title })
      await refreshProjects(active.id)
    } catch (e) {
      setError(msgOf(e))
    }
  }
  // ── Match viewer: the club match page's viewer mode for this game,
  // docked beside the work or in its own window. Seeks go through
  // useViewerSeek (pages/match/viewerBridge), which holds each one until
  // the viewer says it is ready — never posted into a page still loading.
  const [viewer, setViewer] = useState<'off' | 'docked' | 'window'>('off')
  const viewerFrame = useRef<HTMLIFrameElement | null>(null)
  const viewerWindow = useRef<Window | null>(null)
  const viewerUrl = game ? matchViewerUrl(game.game_code) : null
  const target = useCallback((): Window | null => {
    if (viewer === 'window') {
      const w = viewerWindow.current
      return w && !w.closed ? w : null
    }
    return viewer === 'docked' ? viewerFrame.current?.contentWindow ?? null : null
  }, [viewer])
  // No viewer to send to (closed, or its window shut by hand): dock one.
  const dock = useCallback(() => setViewer('docked'), [])
  const { seek: seekVideo, reset: resetViewer, notice: viewerNotice } = useViewerSeek(target, dock)
  // A new game closes the viewer, popped-out window included: it would
  // otherwise go on showing the old match beside the new one's analysis.
  // Only a switch counts: the first game arriving must not clear a seek
  // already made for it.
  const viewerGame = useRef<number | null>(null)
  useEffect(() => {
    const was = viewerGame.current
    viewerGame.current = gameId
    if (was === null || was === gameId) return
    setViewer('off')
    viewerWindow.current?.close()
    viewerWindow.current = null
    resetViewer()
  }, [gameId, resetViewer])
  const dockViewer = () => { resetViewer(); setViewer('docked') }
  const popOutViewer = () => {
    if (!viewerUrl || !game) return
    resetViewer()
    // through the one opener: grant in the address, window per view-as
    viewerWindow.current = openViewerWindow(viewerUrl, String(game.game_id))
    setViewer(viewerWindow.current ? 'window' : 'docked')
  }
  const closeViewer = () => { resetViewer(); setViewer('off') }

  const removeCanvas = async () => {
    if (!active?.can_edit) return
    if (!window.confirm(`Delete "${active.title}"? Its cards and notes go with it; this cannot be undone.`)) return
    try {
      await deleteProject(active.id)
      await refreshProjects()
    } catch (e) {
      setError(msgOf(e))
    }
  }

  if (access == null) return <p className="lab-sub">Loading the Analysis Room&hellip;</p>
  if (!access.enabled) {
    return (
      <>
        <h1 className="page-title">Analysis Room</h1>
        <p className="page-sub">
          The Analysis Room isn&rsquo;t switched on for {session.org?.name ?? 'this club'} yet.
          Ask your Priori contact to turn it on.
        </p>
      </>
    )
  }

  const tabs = TABS.filter((t) => access.capabilities.includes(t.cap))
  // An unknown or ungranted address lands on the first VISIBLE tab — never
  // on a hidden one, which has no tab to show it is open.
  const current = tabs.find((t) => t.tab === tab) ?? tabs.find((t) => !t.hidden)
  const canAsk = access.capabilities.includes('ask_ai')
  // The canvas owns the table browser and is the only thing that sets
  // toggleTablesRef, so the Tables button exists only where the canvas is
  // mounted (as the Lab's, #650) — mirrors the render condition below.
  const canvasMounted = current?.tab === 'collaborate' && !!game
    && orgId != null && !!active
  const canTables = access.capabilities.includes('tables')

  return (
    <div className={`insights-lab${chatOpen && canAsk ? ' pg-chat-open' : ''}`}>
      {/* Row 1 is the same on every tab — title, sections, game — so moving
          between tabs never shifts it. Whatever belongs to one tab (the
          canvas controls, Tables, Ask AI) lives on row 2. */}
      <div className="ws-head">
        <h1 className="page-title">Analysis Room</h1>
        <nav className="lab-tabs lab-tabs--inline" aria-label="Analysis Room sections">
          {tabs.filter((t) => !t.hidden).map((t) => (
            <Link key={t.tab}
                  to={{ pathname: t.path, search: searchParams.toString() }}
                  className={t.tab === current?.tab ? 'is-on' : ''}
                  aria-current={t.tab === current?.tab ? 'page' : undefined}>
              {t.label}
            </Link>
          ))}
        </nav>
        <label className="cgp-field ws-head__game">
          <span className="cgp-field__label">Game</span>
          <select className="adm-select" value={gameId ?? ''} aria-label="Game"
                  onChange={(e) => setGameId(Number(e.target.value) || null)}>
            {games == null && <option value="">Loading&hellip;</option>}
            {games?.length === 0 && <option value="">No layer-stack games yet</option>}
            {games?.map((g) => (
              <option key={g.game_id} value={g.game_id}>
                {g.game_code}{g.date ? ` · ${g.date}` : ''}
              </option>
            ))}
          </select>
        </label>
        {viewerUrl && (
          <span className="ws-head__viewer">
            <button className="ac-btn-sm" aria-pressed={viewer === 'docked'}
                    title="The match video beside your work — results with a frame jump to it"
                    onClick={() => (viewer === 'docked' ? closeViewer() : dockViewer())}>
              {viewer === 'docked' ? 'Hide match viewer' : 'Match viewer'}
            </button>
            <button className="ac-btn-sm" title="Open the match viewer in its own window"
                    onClick={popOutViewer}>Pop out</button>
            {viewerNotice && viewer !== 'off' && (
              <span className="pg-note" role="status">{viewerNotice}</span>
            )}
          </span>
        )}
      </div>
      {current?.tab === 'collaborate' && (
        <div className="ws-subhead">
          <label className="cgp-field">
            <span className="cgp-field__label">Canvas</span>
            <select className="adm-select" value={active?.id ?? ''} aria-label="Canvas"
                    onChange={(e) => setActive(projects.find((p) => p.id === Number(e.target.value)) ?? null)}>
              {projects.length === 0 && <option value="">No canvases yet</option>}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}{p.can_edit ? '' : ` · ${p.owner_name ?? 'a teammate'}`}
                  {p.created_at ? ` · ${createdOn(p.created_at)}` : ''}
                </option>
              ))}
            </select>
          </label>
          {!viewingAs && validGameId != null && (
            <>
              <button className="ac-btn ac-btn--primary" title="Start a new canvas"
                      onClick={() => void newCanvas()}>+ New Canvas</button>
              {active?.can_edit && (
                <CanvasMenu onRename={() => void renameCanvas()}
                            onDelete={() => void removeCanvas()} />
              )}
            </>
          )}
          {active && active.can_edit === false && (
            <span className="ac-badge" title="A teammate's canvas — you can explore it; Duplicate to keep your own">
              read-only{active.owner_name ? ` · ${active.owner_name}` : ''}
            </span>
          )}
          <span className="ws-subhead__end">
            {canTables && canvasMounted && (
              <button className="ac-btn-sm" title="Show or hide the table browser"
                      onClick={() => toggleTablesRef.current?.()}>Tables</button>
            )}
            {canAsk && (
              <button className="pg-chat-toggle" onClick={() => setChatOpen((v) => !v)}>
                {chatOpen ? 'Close AI' : 'Ask AI'}
              </button>
            )}
          </span>
        </div>
      )}

      {error && <p className="adm-msg adm-msg--err">{error}</p>}
      {games?.length === 0 && (
        <p className="lab-sub">
          The Analysis Room works on games processed into the layer stack; none of this
          club&rsquo;s games are there yet.
        </p>
      )}

      <div className="cv-main-area">
        <div className="cv-main-content">
          {current?.tab === 'collaborate' && game && orgId != null && (
            active ? (
              <CanvasWorkspace
                key={active.id}
                project={active}
                orgId={orgId}
                gameCode={game.game_code}
                readOnly={active.can_edit === false || viewingAs}
                addCardRef={addCardRef}
                toggleTablesRef={toggleTablesRef}
                canAsk={canAsk}
                onSeekVideo={seekVideo}
                onDraftChange={(draft: CanvasDraft) => {
                  setActive((prev) => prev ? { ...prev, draft } : prev)
                }}
              />
            ) : (
              <div className="cv-empty" role="status">
                <p className="cv-empty__title">
                  {viewingAs ? 'No canvases for this game yet' : 'Create a canvas to get started'}
                </p>
                {/* Viewing as someone is read-only — the server refuses every
                    write, so there is no + New Canvas. Say so: without it the
                    page read as a club user having no way to make one
                    (Linear, 2026-10-26: "bradley users do not have an option
                    to create one"; the screenshot was a view-as). */}
                {viewingAs && (
                  <p data-testid="cv-empty-view-as">
                    You&rsquo;re viewing as {session.impersonating?.email ?? 'this user'},
                    which is read-only, so a canvas can&rsquo;t be created here.
                    Signed in as themselves they see + New Canvas; return to your
                    account to create one yourself.
                  </p>
                )}
                {!viewingAs && (
                  <>
                    <p>To get started querying the tables, first create a new
                      canvas. A canvas is where you ask questions of this match
                      and keep the answers; your club can open it too.</p>
                    <button className="ac-btn ac-btn--primary cv-empty__cta"
                            onClick={() => void newCanvas()}>
                      + New Canvas
                    </button>
                  </>
                )}
              </div>
            )
          )}
          {current?.tab === 'dashboard' && game && (
            <Suspense fallback={<p className="lab-sub">Loading the dashboard&hellip;</p>}>
              <LabDashboard orgId={orgId} gameCode={game.game_code} gameId={game.game_id} />
            </Suspense>
          )}
          {current?.tab === 'dictionary' && (
            <Suspense fallback={<p className="lab-sub">Loading the data dictionary&hellip;</p>}>
              <DataDictionary />
            </Suspense>
          )}
          {current?.tab === 'segment' && game && (
            <Suspense fallback={<p className="lab-sub">Loading the workbench&hellip;</p>}>
              <SegmentWorkbench session={session} gameCode={game.game_code} orgId={orgId} />
            </Suspense>
          )}
          {game && orgId != null && ANALYSIS_TABS.includes(current?.tab as WorkshopTab) && (
            <Suspense fallback={<p className="lab-sub">Loading the analysis&hellip;</p>}>
              {current?.tab === 'attacking' && (
                <AttackingStructure key={game.game_id} orgId={orgId} gameId={game.game_id}
                                    gameCode={game.game_code} onSeekVideo={seekVideo} />
              )}
              {current?.tab === 'defensive' && (
                <DefensiveControl key={game.game_id} orgId={orgId} gameId={game.game_id}
                                  gameCode={game.game_code} onSeekVideo={seekVideo} />
              )}
              {current?.tab === 'provoke' && (
                <ProvokePressing key={game.game_id} orgId={orgId} gameId={game.game_id}
                                 gameCode={game.game_code} onSeekVideo={seekVideo} />
              )}
              {current?.tab === 'formations' && (
                <FormationDynamics key={game.game_id} orgId={orgId} gameId={game.game_id}
                                   gameCode={game.game_code} onSeekVideo={seekVideo} />
              )}
            </Suspense>
          )}
        </div>
        {viewer === 'docked' && viewerUrl && game && (
          <aside className="cv-side-panes" data-testid="ws-match-viewer">
            <div className="cv-pane cv-pane--match">
              <div className="cv-pane__head">
                <strong>Match viewer</strong>
                <span className="cv-plan-dock__actions">
                  <button className="ac-btn-sm" onClick={popOutViewer}>Pop out</button>
                  <button className="ac-btn-sm" aria-label="Close the match viewer"
                          onClick={closeViewer}>&times;</button>
                </span>
              </div>
              {/* The club match page's own viewer mode, same origin: its
                  sync, marks and layers are the real page, not a copy. */}
              <iframe className="cv-pane__frame" src={viewerUrl} ref={viewerFrame}
                      title={`Match viewer — ${game.game_code}`} />
            </div>
          </aside>
        )}
      </div>

      {chatOpen && canAsk && current?.tab === 'collaborate' && (
        <div className="pg-chat-drawer">
          <ChatPanel
            gameCode={game?.game_code ?? null}
            orgId={orgId}
            onAddCard={(question, sql, visual, explanation) => { addCardRef.current?.(question, sql, visual, explanation) }}
          />
        </div>
      )}
    </div>
  )
}

/** "<game> — canvas N" with the first N no canvas on this game uses.
 *  Counting canvases (the old rule) repeats a name once one is deleted;
 *  the server refuses a taken name either way. */
export function nextCanvasTitle(gameCode: string, taken: string[]): string {
  const used = new Set(taken.map((t) => t.trim().toLowerCase()))
  for (let n = 1; ; n += 1) {
    const title = `${gameCode} — canvas ${n}`
    if (!used.has(title.toLowerCase())) return title
  }
}

/** When a canvas was created, for telling canvases apart: "12 Oct 2026". */
export function createdOn(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The tabs that render one of the four analyses. */
const ANALYSIS_TABS: WorkshopTab[] = ['attacking', 'defensive', 'provoke', 'formations']

/** The bare match viewer (MatchVideoOnly: just the video and its
 *  playback controls), for one game. */
export function matchViewerUrl(gameCode: string): string {
  return `/match-room/${pathSeg(gameCode)}?view=video`
}

function msgOf(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e)
}

/** The HTTP status a failed call carried, read off the shape rather than the
 *  class: an ApiError that crossed a bundle boundary is still an answer. */
function statusOf(e: unknown): number | undefined {
  return typeof e === 'object' && e !== null
    ? (e as { status?: unknown }).status as number | undefined : undefined
}


/** Rename / Delete for the active canvas, behind one button. Only rendered for
 *  your own canvas (the server refuses anyone else's). Closes on a choice, on
 *  Escape, and on a click anywhere outside. New canvas is its own button. */
function CanvasMenu({ onRename, onDelete }: {
  onRename: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLSpanElement | null>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  const pick = (fn: () => void) => () => { setOpen(false); fn() }
  return (
    <span className="ws-menu" ref={box}>
      <button className="ac-btn ws-menu__dots" aria-haspopup="menu" aria-expanded={open}
              aria-label="Canvas actions" title="Rename or delete this canvas"
              onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">&#x22EF;</span>
      </button>
      {open && (
        <div className="ws-menu__list" role="menu" aria-label="Canvas actions">
          <button role="menuitem" onClick={pick(onRename)}>Rename&hellip;</button>
          <button role="menuitem" className="ws-danger" onClick={pick(onDelete)}>Delete&hellip;</button>
        </div>
      )}
    </span>
  )
}
